import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { AlgoliaSearch } from "@/services/algoliaSearch"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqQueryOptions } from "../../shared/utils/groqQueryOptions"
import { groqShortProductProjection } from "../sanity/product.projection"
import { categoryGroupFilter } from "../utils/filters"

/**
 * Query for searching products.
 */
export const queryAlgoliaProductSearch = {
  /**
   * Resolver function for productSearch query.
   * @param {any} parent - The parent resolver.
   * @param {Object} args - The query arguments.
   * @param {Object} context - The resolver context.
   * @param {Object} info - The query info.
   * @returns {Object} - The search results.
   */
  async algoliaProductSearch(parent, args, context, info) {
    const {
      searchTerm,
      selectedFacets,
      lang = AppSettings.defaultLocale(),
      cdn = true,
      page,
      showAll,
      categoryId,
    } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRODUCTS,
      operation: Operations.READ,
    })

    if (!searchTerm && !selectedFacets) throw new GraphQLError("Invalid search string for selected field")

    const searchReadyFacets = mapSearchReadyFacets(selectedFacets)

    const algoliaClient = AlgoliaSearch.client()

    const usedPage = page ? Number(page) - 1 : 0

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    try {
      const hasProducts = await algoliaClient.searchSingleIndex({
        indexName: "products",
        searchParams: { query: "" },
      })
      if (!hasProducts) throw new Error("No products found")

      const productsResponse = await algoliaClient.searchSingleIndex({
        indexName: "products",
        searchParams: {
          query: searchTerm,
          page: usedPage,
          maxValuesPerFacet: 1000,
          clickAnalytics: true,
          facets: ["*"],
          ...(searchReadyFacets?.length > 0 && { facetFilters: searchReadyFacets }),
          ...(categoryId && { filters: categoryGroupFilter(categoryId) }),
        },
      })

      const responseKeys = getResponseKeys(productsResponse)
      if (responseKeys.lenght === 0) throw new GraphQLError("No products found")

      const sanityProducts = await getProductsFromKeyArray(client, responseKeys)

      if (sanityProducts?.length === 0) throw new GraphQLError("No products found")

      const facets = getFacets(productsResponse?.facets)

      return {
        items: sanityProducts,
        facets,
        limit: productsResponse?.hitsPerPage || 0,
        total: productsResponse?.nbHits || 0,
        count: sanityProducts?.length || 0,
        totalPages: productsResponse?.nbPages || 0,
      }
    } catch (error) {
      console.warn("Algolia search failed", error)
      if (showAll || searchTerm.length === 0) {
        // not something we can do with sanity
        return {
          items: [],
          facets: [],
          limit: 0,
          total: 0,
          count: 0,
          totalPages: 0,
        }
      }

      // in case algolia is down or values are missing, fallback to sanity
      const products = await getSanityProducts({
        searchString: searchTerm,
        page: usedPage,
        client,
      })
      return products
    }
  },
}

const mapSearchReadyFacets = (selectedFacets) => {
  if (!selectedFacets || selectedFacets.length === 0) return []

  let facetFilters = []

  for (const { key, value } of selectedFacets) {
    if (!key || !value || value.length === 0) continue

    if (value.length > 1) {
      for (let i = 0; i < value.length; i++) {
        facetFilters.push(`${key}:${value[i]?.value}`)
      }
      continue
    }

    facetFilters.push(`${key}:${value[0]?.value}`)
  }

  return facetFilters
}

/**
 * Provided array of keys, retrieves all products from sanity
 *
 * @param {SanityClient} client
 * @param {[string]} keys
 *
 * @returns {Promise<[products]>}
 */
export const getProductsFromKeyArray = async (client, keys) => {
  const { draftsFilter } = groqQueryOptions({ includeDrafts: true })

  const products = await client.fetch(
    groq`*[_type=="product" && key in $keys ${draftsFilter}] {
      ...,
      ${groqShortProductProjection()}
    }`,
    { keys },
  )

  const algoliaArrangedProducts = keys.map((key) => products.find((product) => product.key === key))

  return algoliaArrangedProducts
}

const getResponseKeys = (response) => {
  if (!response || response?.hits?.lenght === 0) return []

  return response.hits.map(({ key }) => key)
}

const getFacets = (facets) => {
  if (!facets || facets.length === 0) return []

  const mappedFacets = []

  for (const [key, value] of Object.entries(facets)) {
    const keyFacet = []
    for (const [valueKey, valueAmount] of Object.entries(value)) {
      keyFacet.push({
        option: valueKey,
        amount: valueAmount,
      })
    }
    mappedFacets.push({ key, values: keyFacet })
  }

  return mappedFacets
}

const getSanityProducts = async ({ searchString, page, client }) => {
  const isNumber = !isNaN(searchString)
  const orphanArticlesFilter = `&& !(key match "orphan-article"+'*')`

  const matchPrefix = `match '*'+$term+'*'`

  const matchers = isNumber ? [`key ${matchPrefix}`] : [`title ${matchPrefix}`, `shortDescription ${matchPrefix}`]

  const searchValue = `${matchers.join(" || ")}`

  // 9 items per page
  const limit = 9
  const offset = Number(page) * limit

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    offset,
    limit,
    includeDrafts: true,
  })

  const totalCountQuery = groq`count(*[_type=="product" && ${searchValue}${draftsFilter}${orphanArticlesFilter}])`
  const productsQuery = groq`*[_type=="product" && ${searchValue}${draftsFilter}${orphanArticlesFilter}] | score(
    boost(key match $term, 3),
    boost(title match $term, 2),
    boost(shortDescription match $term, 1)
  )${pipesAndLimits}
   {
    ...,
    ${groqShortProductProjection()}
  }`

  const [totalCount, productItems] = await Promise.all([
    client.fetch(totalCountQuery, { term: searchString }),
    client.fetch(productsQuery, { term: searchString }),
  ])

  return {
    items: productItems,
    facets: [],
    limit,
    total: totalCount,
    count: productItems.length,
    totalPages: Math.ceil(totalCount / limit),
  }
}
